import { useTranslation } from 'react-i18next'
import { setLocale, type Locale } from '../../i18n'

interface LocaleSwitcherProps {
  onLocaleChange?: (locale: Locale) => void | Promise<void>
}

const OPTIONS: ReadonlyArray<{ locale: Locale; flag: string }> = [
  { locale: 'ar', flag: '🇸🇦' },
  { locale: 'en', flag: '🇺🇸' },
]

export function LocaleSwitcher({ onLocaleChange }: LocaleSwitcherProps) {
  const { t, i18n } = useTranslation()
  const current = i18n.language === 'en' ? 'en' : 'ar'

  function change(locale: Locale) {
    setLocale(locale)
    void onLocaleChange?.(locale)
  }

  return (
    <div
      role="group"
      aria-label={t('localeSwitcher.label')}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: 4,
        border: '1px solid var(--border-subtle)',
        background: 'rgba(255,255,255,0.78)',
        borderRadius: 999,
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      {OPTIONS.map(option => {
        const active = option.locale === current
        return (
          <button
            key={option.locale}
            type="button"
            onClick={() => change(option.locale)}
            aria-pressed={active}
            style={{
              border: 'none',
              background: active ? 'var(--brand-100)' : 'transparent',
              color: active ? 'var(--brand-700)' : 'var(--fg-muted)',
              fontFamily: 'inherit',
              fontWeight: 800,
              fontSize: 12.5,
              padding: '7px 11px',
              borderRadius: 999,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <span aria-hidden="true">{option.flag}</span>
            <span>{t(`common.locales.${option.locale}`)}</span>
          </button>
        )
      })}
    </div>
  )
}
